import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { cn } from '../../shared/utils/cn';
import EventCard from '../../shared/components/EventCard';
import useHorizontalScroller from '../../shared/hooks/useHorizontalScroller';
import * as API from '../../api';

function ScrollButton({ direction, onClick, disabled }) {
  const Icon = direction === 'left' ? ChevronLeftIcon : ChevronRightIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      className={cn(
        'hidden h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-ink shadow-card transition-all duration-200 lg:flex',
        'hover:border-brand-500 hover:text-brand-500 hover:shadow-card-hover',
        disabled && 'cursor-not-allowed opacity-40 hover:border-slate-200 hover:text-ink hover:shadow-card'
      )}
    >
      <Icon className="h-5 w-5 rtl:rotate-180" />
    </button>
  );
}

export default function Featured() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { scrollerRef, canPrev, canNext, progress, scrollByDir } =
    useHorizontalScroller([events.length, loading]);

  useEffect(() => {
    let mounted = true;
    API.HoteEvents(1, 12)
      .then((res) => mounted && setEvents(res?.data || []))
      .catch(() => mounted && setEvents([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  return (
    <section className="relative bg-surface-muted py-12 sm:py-16">
      <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4">
          <div className="max-w-xl">
            <h2 className="font-display text-[19px] font-semibold tracking-tight text-ink whitespace-nowrap sm:text-[28px] sm:whitespace-normal lg:text-3xl">
              {t('home.featuredTitle')}
            </h2>
            <p className="mt-1.5 hidden text-sm text-ink-muted sm:block">
              {t('home.featuredSubtitle')}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <ScrollButton direction="left" onClick={() => scrollByDir('left')} disabled={!canPrev} />
            <ScrollButton direction="right" onClick={() => scrollByDir('right')} disabled={!canNext} />
          </div>
        </div>

        <div className="relative mt-6">
          <div
            ref={scrollerRef}
            className="flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="shrink-0 snap-start overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card"
                  style={{ width: '288px' }}
                >
                  <div className="aspect-[4/3] animate-pulse bg-slate-100" />
                  <div className="space-y-2 p-4">
                    <div className="h-4 w-4/5 animate-pulse rounded bg-slate-100" />
                    <div className="h-3 w-2/5 animate-pulse rounded bg-slate-100" />
                    <div className="h-3 w-1/3 animate-pulse rounded bg-slate-100" />
                    <div className="mt-3 h-6 w-2/5 animate-pulse rounded bg-slate-100" />
                  </div>
                </div>
              ))
            ) : events.length === 0 ? (
              <p className="py-12 text-center text-sm text-ink-subtle">
                {t('home.featuredEmpty')}
              </p>
            ) : (
              events.map((e) => <EventCard key={e.id} event={e} />)
            )}
          </div>
        </div>

        {/* Mobile-only scroll progress indicator */}
        {!loading && events.length > 0 && (
          <div className="mt-4 flex items-center justify-center gap-3 lg:hidden">
            <div className="relative h-1 w-32 overflow-hidden rounded-full bg-slate-200">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-brand-500 transition-[width] duration-150 rtl:left-auto rtl:right-0"
                style={{ width: `${Math.max(8, progress * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* desktop "view all" pill below the strip */}
        {!loading && events.length > 0 && (
          <div className="mt-8 flex justify-center">
            <Link
              to="/events"
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-500 bg-white px-5 py-2.5 text-sm font-semibold text-brand-500 transition-all duration-200 hover:bg-brand-500 hover:text-white"
            >
              {t('home.featuredViewAll', 'View all experiences')}
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden>
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
